import { QueryClient } from '@tanstack/react-query';

import { isAppError } from '@/api/errors';

/**
 * Statuses that will never succeed on a retry: the request itself is the
 * problem, so repeating it just delays the error the user needs to see.
 */
const NON_RETRYABLE = new Set([400, 401, 403, 404, 405, 409, 410, 422, 429]);

/**
 * A factory rather than a module-level singleton, so each test gets a clean
 * cache and tests can't leak state into one another.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,

        retry: (failureCount, error) => {
          const status = isAppError(error) ? error.status : 0;
          if (NON_RETRYABLE.has(status)) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8_000),

        // Only genuinely unexpected failures escalate to an error boundary.
        // Anything a page can explain in place (401, 404, 422) is handled
        // inline so the user keeps their navigation and context.
        throwOnError: (error) => isAppError(error) && error.status >= 500,
      },
      mutations: {
        // Never silently re-send a sign-in or a password reset.
        retry: false,
      },
    },
  });
}
